import { WorkInstruction } from '@/tsort/enum/work-instruction.enum';
import { ScanSystemField } from '@/tsort/model/response/scan-system-field.model';
import AppConstants from '@/common/constants/app.constants';
import store from '../store';
import { WorkTypeSorting } from '@/tsort/enum/work-type-sorting.enum';

export default class SortingUtils {
  // Get field name of scan code field
  public static getScanCodeFieldName(scanSystemFields: Array<ScanSystemField> | null) {
    let scanCodeFieldName = '';
    const scanCodeField = scanSystemFields
      ?.filter((field) => field.fieldVisible === 1)
      .find((e) => e.fieldId === AppConstants.SCAN_CODE_FIELD_ID);
    if (scanCodeField) {
      scanCodeFieldName = scanCodeField?.fieldName;
    }
    return scanCodeFieldName;
  }

  // Get index of scan code field in field list
  public static getScanCodeFieldIndex(scanSystemFields: Array<ScanSystemField> | null) {
    const scanCodeIndex = scanSystemFields
      ?.filter((field) => field.fieldVisible === 1)
      .findIndex((e) => e.fieldId === AppConstants.SCAN_CODE_FIELD_ID);
    return scanCodeIndex;
  }

  /**
   * リストを条件でフィルタリングします。
   *
   * @param {Array<T>} datas フィルタリングするリスト
   * @param {Partial<Record<keyof T, any>>} conditions フィルタリング条件
   * @returns {Array<T>} フィルタリングされたリスト
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static filterListByCondition<T>(datas: Array<T>, conditions: Partial<Record<keyof T, any>>): Array<T> {
    const filteredList = datas.filter((item) => {
      return Object.keys(conditions).every((key) => {
        const conditionKey = key as keyof T;
        const conditionValue = conditions[conditionKey];

        // マルチコンディションのハンドル
        if (typeof conditionValue === 'function') {
          return conditionValue(item[conditionKey]);
        }

        // 単一条件のハンドル
        if (conditionValue !== null && conditionValue !== undefined) {
          return item[conditionKey] === conditionValue;
        }
        return true;
      });
    });
    return filteredList;
  }

  /**
   * 作業指示を取得する。
   * @returns 作業指示のソーティング
   */
  static get workInstruction(): WorkInstruction {
    return store.getters['SortStore/workInstruction'];
  }

  /**
   * 作業タイプのソーティングを取得します。
   * @returns 作業タイプのソーティング
   */
  static get workTypeSorting(): WorkTypeSorting {
    return store.getters['SortStore/workTypeSorting'];
  }

  /**
   * マルチソーティングかどうかを判定します。
   *
   * @returns {boolean} マルチソーティングであれば true、そうでなければ false
   */
  public static get isNoWorkInstruction(): boolean {
    return this.workInstruction === WorkInstruction.NO_WORK_INSTRUCTION;
  }

  /**
   * 並列ソーティングかどうかを判定します。
   *
   * @returns {boolean} 並列ソーティングであれば true、そうでなければ false
   */
  public static get isParallelSorting(): boolean {
    return this.workInstruction === WorkInstruction.PARALLEL_SORTING;
  }

  /**
   * シングルソーティングかどうかを判定します。
   *
   * @returns {boolean} シングルソーティングであれば true、そうでなければ false
   */
  public static get isSingleSorting(): boolean {
    return this.workTypeSorting === WorkTypeSorting.SINGLE_SORTING;
  }

  /**
   * マルチソーティングかどうかを判定します。
   *
   * @returns {boolean} シングルソーティングであれば true、そうでなければ false
   */
  public static get isMultiSorting(): boolean {
    return this.workTypeSorting === WorkTypeSorting.MULTI_SORTING;
  }

  /**
   * マニュアルソーティングかどうかを判定します。
   *
   * @returns {boolean} マニュアルソーティングであれば true、そうでなければ false
   */
  public static get isHandSorting(): boolean {
    return this.workTypeSorting === WorkTypeSorting.MANUAL_SORTING;
  }

  /**
   * このメソッドは、並列ソーティングとマルチソーティングの両方が有効であるかどうかを判定します。
   *
   * @returns 並列マルチソーティングが有効な場合は true、そうでない場合は false を返します
   */
  public static get isParallelMultiSorting(): boolean {
    return this.isParallelSorting && this.isMultiSorting;
  }

  /**
   * このメソッドは、並列ソーティングと単一ソーティングの両方が有効であるかどうかを判定します。
   *
   * @returns 並列単一ソーティングが有効な場合は true、そうでない場合は false を返します
   */
  public static get isParallelSingleSorting(): boolean {
    return this.isParallelSorting && this.isSingleSorting;
  }

  /**
   * このメソッドは、作業命令がなく、単一ソーティングが有効であるかどうかを判定します。
   *
   * @returns 作業命令がなく、単一ソーティングが有効な場合は true、そうでない場合は false を返します
   */
  public static get isNoTaskSingleSorting(): boolean {
    return this.isNoWorkInstruction && this.isSingleSorting;
  }

  /**
   * このメソッドは、作業命令がなく、複数ソーティングが有効であるかどうかを判定します。
   *
   * @returns 作業命令がなく、複数ソーティングが有効な場合は true、そうでない場合は false を返します
   */
  public static get isNoTaskMultiSorting(): boolean {
    return this.isNoWorkInstruction && this.isMultiSorting;
  }
}
